import {POSITIONS_LIST} from './static-data/lists';
import { Position } from './interfaces/positions';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { of, Observable } from 'rxjs';
import {
  AmenityListsByCategory,
  List,
  SelectableItem,
} from '../uas/editing/targeting/targeting-data.interface';

@Injectable({ providedIn: 'root' })
export class ListsService {
  path: string = '/api';
  constructor(private apiService: ApiService) { }

  // TODO: REVIEW: this endpoint's return type is polymorphic on `type` — a flat
  // `List[]` for every type except `amenity`, which returns lists grouped by
  // category (AmenityListsByCategory). The overloads below make each call site
  // type-correct, but a cleaner fix is to split the amenity path into its own
  // method (e.g. getAmenityLists(): Observable<AmenityListsByCategory>) so a
  // single method never carries two shapes.
  getLists(partnerId: number | string, type: 'amenity'): Observable<AmenityListsByCategory>;
  getLists(partnerId: number | string, type: string): Observable<List[]>;
  getLists(partnerId: number | string, type: string): Observable<List[] | AmenityListsByCategory> {
    return this.apiService.get<List[] | AmenityListsByCategory>(`${this.path}/airlines/${partnerId}/${type}_lists`);
  }

  getList(partnerId: number | string, type: string, listId: string): Observable<List> {
    return this.apiService.get<List>(`${this.path}/${type}_lists/${listId}`);
  }

  getStaticList(type: string): Observable<Position[]> | undefined {
    if (type === 'positions') {
      return of(POSITIONS_LIST);
    }
    return undefined;
  }

  // `list` is the mutable draft buffer the edit forms build up; kept `any` because
  // it mixes pre-save inputs (added_items) with per-type output arrays and spans
  // all list types — a real draft type belongs on each form, not here. Returns the saved List.
  saveList(partnerId: number | string, type: string, list: any): Observable<List> {
    const configuredList = this.configureAddedItems(type, list);
    return this.apiService
      .post<List>(`${this.path}/airlines/${partnerId}/${type}_lists`, { data: configuredList });
  }

  updateList(partnerId: number | string, type: string, list: any): Observable<List> {
    const configuredList = this.configureAddedItems(type, list);
    return this.apiService
      .put<List>(`${this.path}/${type}_lists/${list.id}`, { data: configuredList });
  }

  deleteList(partnerId: number | string, type: string, listId: number | string): Observable<unknown> {
    return this.apiService.delete(`${this.path}/${type}_lists/${listId}`);
  }

  // Internal polymorphic mutator: folds the draft's `added_items` into the
  // per-type code/id arrays. Stays loose (`any`) — the working buffer's shape
  // varies by type and by call site.
  configureAddedItems(type: string, list: any): any {
    if (type === 'airport') {
      if (!list.airport_codes) {
        list['airport_codes'] = [];
      }
      list.airport_codes = list.added_items?.map((item: SelectableItem) => item.code) ?? [];
    } else if (type === 'country') {
      if (!list.country_codes) {
        list['country_codes'] = [];
      }
      list.country_codes = list.added_items?.map((item: SelectableItem) => item.code) ?? [];
    } else if (type === 'subfleet') {
      if (!list.subfleet_ids) {
        list['subfleet_ids'] = [];
      }
      list.subfleet_ids = list.added_items?.map((item: SelectableItem) => item.id) ?? [];
    } else if (type === 'equipment_class') {
        list['equipment_class_values'] = [];
        list.added_items?.forEach((item: SelectableItem) => {
          list.equipment_class_values.push(item.name);
      });
    }else if (type === 'terminal') {
      if (!list.terminal_codes) {
        list['terminal_codes'] = [];
      }
      list.terminal_codes = list.added_items?.map((item: SelectableItem) => item.code) ?? [];
    } else if (type === 'amenity') {
      if (!list.display_texts) {
        list['display_texts'] = [];
      }
      list.display_texts = list.added_items ?? [];
    } else if (type === 'tail') {
      if (!list.tail_ids) {
        list['tail_ids'] = [];
      }
      list.tail_ids = list.added_items?.map((item: SelectableItem) => item.id) ?? [];
    } else if (type === 'origin_and_destination') {
      if (list.new_routes && list.new_routes.length) {
        list.routes = list.routes.concat(list.new_routes);
      }
    }
    return list;
  }
}
